import { useDraggable } from '@dnd-kit/react'
import type { TestGroupingItem } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { isEmptyRtValue, rtValueToText, textToRtValue } from '@vedalib/rich-text'
import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useRef, useCallback, useMemo, useEffect } from 'react'
import { useContext } from 'use-context-selector'

import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import RichText from 'components/form/RichText'
import { RichTextViewer } from 'components/form/RichText/RichTextViewer'
import { IconButton } from 'components/uiKit/Button'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'

import TestGroupingContext from '../../TestGroupingContext'
import { getNavPositionStyle } from '../../helper'
import { PortalAnswerItemStyled, AnswerDragIconStyled } from '../../styled/TestGroupingStyled'
import * as s from './PortalAnswerItem.module.scss'

type ItemProps = {
  item: TestGroupingItem
  stackIndex?: number
  withFadeIn?: boolean
  answers: TestGroupingItem[]
  currentIndex: number
  index: number
  onDraggingChange?: (isDragging: boolean) => void
}

const PortalAnswerItem = ({
  item,
  stackIndex = 0,
  withFadeIn = false,
  answers,
  currentIndex,
  index,
  onDraggingChange,
}: ItemProps) => {
  const {
    styles,
    font,
    selectedAnswerId,
    setSelectedAnswerId,
    setSelectedGroupId,
    isFill,
    value,
    isActiveElement,
    activeIndex,
    onLabelSelect,
    onChangeLabel,
    onDeleteAnswer,
    onDuplicateAnswer,
    shuffledAnswers,
    mode,
    blockResult,
  } = useContext(TestGroupingContext)
  const items = value.items
  const isDragDistanceRef = useRef(false)

  const isActive = currentIndex === index
  const { ref: draggableRef, isDragging } = useDraggable({
    id: item.value,
    data: item,
    disabled: !isActive || !!blockResult,
  })

  useEffect(() => {
    onDraggingChange?.(isDragging)
  }, [isDragging, onDraggingChange])

  const isSelected = selectedAnswerId === item.value

  const { labelName, labelPath, groupValue, answersInGroup, groupIndex } = useMemo(() => {
    const gIndex = items.findIndex((group) => group.items?.some((a) => a.value === item.value))
    const group = gIndex !== -1 ? items[gIndex] : null
    const aIndex = group?.items?.findIndex((a) => a.value === item.value) ?? -1

    return {
      labelName: `items.${gIndex}.items.${aIndex}.label`,
      labelPath: ['items', gIndex, 'items', aIndex, 'label'],
      groupValue: group?.value ?? '',
      answersInGroup: group?.items?.length ?? 0,
      groupIndex: gIndex,
    }
  }, [items, item.value])

  const handleDuplicate = useCallback(() => {
    if (groupValue) {
      onDuplicateAnswer(groupValue, item.value)
    }
  }, [groupValue, item.value, onDuplicateAnswer])

  const handleDelete = useCallback(() => {
    if (groupValue) {
      onDeleteAnswer(groupValue, item.value)
      setSelectedAnswerId(undefined)
    }
  }, [groupValue, item.value, onDeleteAnswer, setSelectedAnswerId])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isFill) {
        return
      }

      const { clientX, clientY } = e
      const clickStart = { x: clientX, y: clientY }
      isDragDistanceRef.current = false

      const onMouseMove = (event: MouseEvent) => {
        const isDrag =
          Math.abs(event.clientX - clickStart.x) > 5 || Math.abs(event.clientY - clickStart.y) > 5
        if (isDrag) {
          isDragDistanceRef.current = true
        }
      }

      const onMouseUp = () => {
        if (!isDragDistanceRef.current) {
          setSelectedGroupId(undefined)
          setSelectedAnswerId(isSelected ? undefined : item.value)
        }
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [isFill, isSelected, item.value, setSelectedAnswerId, setSelectedGroupId],
  )

  const handleTextMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isFill) {
        return
      }
      e.stopPropagation()
      setSelectedAnswerId(item.value)
      setSelectedGroupId(undefined)
      onLabelSelect(labelName, e)
    },
    [isFill, labelName, onLabelSelect, item.value, setSelectedAnswerId, setSelectedGroupId],
  )
  const imageUrl = useUrlByUsage(item.image, FileUsageTypeEnum.image)
  const dragIconBg = styles.dragButton.backgroundImage
  const hasAnswerImage = !!item.image?.id

  const backgroundImage = (hasAnswerImage && `url(${imageUrl})`) || undefined
  const textStyles = item.label && { ...font?.base }
  const hasLabel = item.label && !isEmptyRtValue(item.label)
  const labelClasses = cn(s.label, { [s.placeholder]: !hasLabel && !isFill })
  const showPlaceholder = !hasLabel && !(item?.image?.id && hasAnswerImage)

  const allAnswers = items.flatMap((group) => group.items || [])

  const currentAnswer = answers[currentIndex]
  const globalIndex = currentAnswer
    ? allAnswers.findIndex((a) => a.value === currentAnswer.value) + 1
    : 0

  const counterCurrent = index + 1
  const counterTotal = shuffledAnswers.length

  const childStyles = {
    ...styles.common,
    ...styles.answer,
    backgroundImage,
    backgroundSize: getObjectFitDivMap(item.image?.objectFit),
    zIndex: isDragging ? 10 : stackIndex,
    ...(withFadeIn && { animation: 'fadeIn 0.3s ease' }),
  }

  const child = (
    <PortalAnswerItemStyled
      $isDragging={isDragging}
      $styles={styles}
      aria-label={rtValueToText(item.label) || item?.image?.accessibility || item.value}
      data-grouping-id={item.value}
      onMouseDown={handleMouseDown}
      ref={draggableRef}
      style={childStyles}
      tabIndex={isActive ? 0 : -1}
    >
      <div
        className={s.mask}
        style={{
          backgroundColor: item.overlay || undefined,
          borderRadius: styles.common.borderRadius,
        }}
      />
      <div
        className={labelClasses}
        style={{
          padding: styles.indents.padding,
          justifyContent: styles.answer.alignItems,
        }}
      >
        <div className={s.textInner} onMouseDown={handleTextMouseDown} style={textStyles}>
          {isFill ? (
            <RichText
              active={isActiveElement && activeIndex === labelName}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={labelName}
              onChange={(val) => onChangeLabel(val, labelPath)}
              placeholder={t('elements.testGrouping.answer.placeholder')}
              styles={font}
              value={item.label || textToRtValue('')}
            />
          ) : showPlaceholder ? (
            t('elements.testGrouping.answer.placeholder')
          ) : (
            <RichTextViewer styles={font} value={item.label} />
          )}
        </div>
      </div>
      <div
        className={s.iconWrapper}
        style={getNavPositionStyle(styles.dragButton?.iconsAlign, styles.indents?.padding)}
        aria-hidden
      >
        <AnswerDragIconStyled $isDragging={isDragging} $styles={styles}>
          {!dragIconBg && <Icon name='builderDrug' size={KitSize.S} />}
        </AnswerDragIconStyled>
      </div>
      <div
        className={s.navigation}
        style={getNavPositionStyle(styles.answerNavigation?.iconsAlign, styles.indents?.padding)}
      >
        <AnimatePresence mode='popLayout'>
          <motion.div
            animate={{ opacity: 1 }}
            className={s.counter}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={`${counterCurrent}-${counterTotal}`}
            style={font?.answerNumbering}
            transition={{ duration: 0.2 }}
          >
            {counterCurrent}/{counterTotal}
          </motion.div>
        </AnimatePresence>
      </div>
      {isFill && groupIndex !== -1 && (
        <div className={s.groupBadge}>
          {t('elements.grouping.tags.group')} {groupIndex + 1}
        </div>
      )}
      {isSelected && isFill && index === currentIndex && (
        <>
          <AbsolutePortal name='answerBadge'>
            <div className={s.outline}>
              <div className={s.badge}>
                {t('elements.grouping.tags.answer')} {globalIndex}
              </div>
            </div>
          </AbsolutePortal>
          <AbsolutePortal name='answerToolbar' placement='bottomLeft' translateY='8px'>
            <div className={s.toolbar} onClick={(e) => e.stopPropagation()}>
              <Tooltip overlay={t('elements.testGrouping.actions.duplicateAnswer')}>
                <IconButton
                  disabled={answersInGroup >= 10}
                  icon='otherCopy'
                  iconSize={KitSize.XS}
                  name='duplicateAnswer'
                  onClick={handleDuplicate}
                  size={KitSize.XS}
                  styleType='ghost'
                />
              </Tooltip>
              <Tooltip overlay={t('elements.testGrouping.actions.deleteAnswer')}>
                <IconButton
                  icon='trashDeleteBin'
                  iconSize={KitSize.XS}
                  name='deleteAnswer'
                  onClick={handleDelete}
                  size={KitSize.XS}
                  styleType='ghost'
                />
              </Tooltip>
            </div>
          </AbsolutePortal>
        </>
      )}
    </PortalAnswerItemStyled>
  )

  return child
}

export default PortalAnswerItem
